import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Badge } from '@pesto/ui/components/Badge';
import { Card } from '@pesto/ui/components/Card';
import { toast } from '@pesto/ui/components/Toaster';
import { Typography } from '@pesto/ui/components/Typography';
import { useMemo } from 'react';
import { z } from 'zod';

import type {
  JobStatusResponse,
  UpdateJobRequestUpdates,
  UpdateJobRequestUpdatesMeta,
} from '../../../__generated__/rest/api';
import type { ModalProps } from '../../types/modal.types';

import { UpdateJobRequestUpdatesState, useUpdateJob } from '../../../__generated__/rest/api';
import { JOB_STATE_LABELS, JOB_STATE_VARIANTS } from '../../../pages/ObanJobs/hooks/useObanJobsListConfig';
import { createFormComponent } from '../../utils/create-form-component';

const updateObanJobFormSchema = z.object({
  state: z.string().optional(),
  queue: z.string().optional(),
  worker: z.string().optional(),
  attempt: z.coerce.number().optional(),
  maxAttempts: z.coerce.number().optional(),
  scheduledAt: z.string().optional(),
  attemptedAt: z.string().optional(),
  completedAt: z.string().optional(),
  cancelledAt: z.string().optional(),
  discardedAt: z.string().optional(),
  priority: z.coerce.number().optional(),
  tags: z.string().optional(),
  args: z
    .string()
    .optional()
    .transform((val) => {
      if (!val?.trim()) return undefined;

      try {
        return JSON.parse(val);
      } catch {
        throw new z.ZodError([{ code: 'custom', message: 'Must be valid JSON', path: ['args'] }]);
      }
    }),
  meta: z
    .string()
    .optional()
    .transform((val) => {
      if (!val?.trim()) return undefined;

      try {
        return JSON.parse(val);
      } catch {
        throw new z.ZodError([{ code: 'custom', message: 'Must be valid JSON', path: ['meta'] }]);
      }
    }),
});

export type UpdateObanJobFormValues = z.infer<typeof updateObanJobFormSchema>;

type ExtendedJobStatus = JobStatusResponse & {
  maxAttempts?: number | null;
  priority?: number | null;
  tags?: string[] | null;
  meta?: UpdateJobRequestUpdatesMeta | null;
};

type UpdateObanJobPayload = {
  jobId: string;
  job: ExtendedJobStatus | null;
  onSuccess?: () => Promise<void> | void;
};

const STATE_LABEL_MAP: Record<UpdateJobRequestUpdatesState, string> = {
  ...JOB_STATE_LABELS,
  scheduled: 'Scheduled',
};

const buildDefaultValues = (job?: ExtendedJobStatus | null): UpdateObanJobFormValues => ({
  state: job?.state ?? '',
  queue: job?.queue ?? '',
  worker: job?.worker ?? '',
  attempt: job?.attempt ?? undefined,
  maxAttempts: job?.maxAttempts ?? job?.attempts ?? undefined,
  scheduledAt: job?.scheduledAt ?? '',
  attemptedAt: job?.attemptedAt ?? '',
  completedAt: job?.completedAt ?? '',
  cancelledAt: job?.cancelledAt ?? '',
  discardedAt: job?.discardedAt ?? '',
  priority: job?.priority ?? undefined,
  tags: job?.tags?.join(', ') ?? '',
  args: job?.args ? JSON.stringify(job.args, null, 2) : '',
  meta: job?.meta ? JSON.stringify(job.meta, null, 2) : '',
});

const tagListSchema = z.array(z.string());

const stateUpdateSchema = z.nativeEnum(UpdateJobRequestUpdatesState);

const parseTagsField = (value: string): string[] => {
  const trimmed = value.trim();

  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = tagListSchema.safeParse(JSON.parse(trimmed));

      if (parsed.success) return parsed.data;
    } catch {}
  }

  return trimmed
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
};

export const UpdateObanJobModal = ({ payload, api }: ModalProps<UpdateObanJobPayload>) => {
  const { jobId, job, onSuccess } = payload;
  const { handleMutation } = useMutationWithToast();
  const updateJobMutation = useUpdateJob();

  const defaultValues = useMemo(() => buildDefaultValues(job), [job]);

  const stateOptions = useMemo(
    () =>
      Object.values(UpdateJobRequestUpdatesState).map((state) => ({
        value: state,
        label: STATE_LABEL_MAP[state],
      })),
    [],
  );

  const formConfig = useMemo<SmartFormConfig<UpdateObanJobFormValues>>(
    () => ({
      schema: updateObanJobFormSchema,
      defaultValues,
      layout: 'grid',
      columns: 2,
      fields: [
        {
          name: 'state',
          label: 'State',
          type: 'select',
          options: stateOptions,
          placeholder: 'Select state',
          defaultValue: defaultValues.state,
        },
        {
          name: 'queue',
          label: 'Queue',
          type: 'text',
          defaultValue: defaultValues.queue,
        },
        {
          name: 'worker',
          label: 'Worker',
          type: 'text',
          defaultValue: defaultValues.worker,
        },
        {
          name: 'attempt',
          label: 'Attempt',
          type: 'number',
          defaultValue: defaultValues.attempt ?? '',
        },
        {
          name: 'maxAttempts',
          label: 'Max Attempts',
          type: 'number',
          defaultValue: defaultValues.maxAttempts ?? '',
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'number',
          defaultValue: defaultValues.priority ?? '',
        },
        {
          name: 'scheduledAt',
          label: 'Scheduled At',
          type: 'text',
          placeholder: '2024-01-01T12:00:00Z',
          defaultValue: defaultValues.scheduledAt,
        },
        {
          name: 'attemptedAt',
          label: 'Attempted At',
          type: 'text',
          placeholder: '2024-01-01T12:00:00Z',
          defaultValue: defaultValues.attemptedAt,
        },
        {
          name: 'completedAt',
          label: 'Completed At',
          type: 'text',
          placeholder: '2024-01-01T12:00:00Z',
          defaultValue: defaultValues.completedAt,
        },
        {
          name: 'cancelledAt',
          label: 'Cancelled At',
          type: 'text',
          placeholder: '2024-01-01T12:00:00Z',
          defaultValue: defaultValues.cancelledAt,
        },
        {
          name: 'discardedAt',
          label: 'Discarded At',
          type: 'text',
          placeholder: '2024-01-01T12:00:00Z',
          defaultValue: defaultValues.discardedAt,
        },
        {
          name: 'tags',
          label: 'Tags',
          type: 'text',
          helperText: 'Comma-separated or JSON array',
          defaultValue: defaultValues.tags,
        },
        {
          name: 'args',
          label: 'Args (JSON)',
          type: 'json',
          componentProps: {
            className: 'min-h-[160px] resize-y font-mono text-sm',
            placeholder: '{}',
          },
          defaultValue: defaultValues.args,
        },
        {
          name: 'meta',
          label: 'Meta (JSON)',
          type: 'json',
          componentProps: {
            className: 'min-h-[160px] resize-y font-mono text-sm',
            placeholder: '{}',
          },
          defaultValue: defaultValues.meta,
        },
      ],
    }),
    [defaultValues, stateOptions],
  );

  const handleSubmit = async (values: UpdateObanJobFormValues) => {
    if (!jobId) {
      throw new Error('Missing job identifier.');
    }

    const updates: UpdateJobRequestUpdates = {};
    const original = buildDefaultValues(job);

    const changedState = stateUpdateSchema.safeParse(values.state);

    if (values.state !== original.state && changedState.success) {
      updates.state = changedState.data;
    }

    if (values.queue !== original.queue) {
      updates.queue = values.queue;
    }

    if (values.worker !== original.worker) {
      updates.worker = values.worker;
    }

    if (values.attempt !== original.attempt) {
      updates.attempt = values.attempt;
    }

    if (values.maxAttempts !== original.maxAttempts) {
      updates.maxAttempts = values.maxAttempts;
    }

    if (values.priority !== original.priority) {
      updates.priority = values.priority;
    }

    const dateFields = ['scheduledAt', 'attemptedAt', 'completedAt', 'cancelledAt', 'discardedAt'] as const;
    dateFields.forEach((field) => {
      if (values[field] !== original[field]) {
        updates[field] = values[field] || null;
      }
    });

    if (values.args !== undefined) {
      updates.args = values.args;
    }

    if (values.meta !== undefined) {
      updates.meta = values.meta;
    }

    if (values.tags !== original.tags) {
      updates.tags = parseTagsField(values.tags || '');
    }

    if (Object.keys(updates).length === 0) {
      toast.error('No changes detected.');

      return;
    }

    await handleMutation({
      mutation: () => updateJobMutation.mutateAsync({ id: jobId, data: { updates } }),
      extractSuccess: (result) => !!result,
      onSuccess: async () => {
        await onSuccess?.();
        api.close();
      },
      debugLabel: 'update-oban-job',
      toast: {
        success: 'Job updated successfully',
        error: 'Failed to update job',
        loading: 'Updating job...',
      },
    });
  };

  if (!jobId) {
    return (
      <Card className="space-y-4 p-6">
        <Typography variant="body" className="text-destructive text-sm">
          Missing job identifier.
        </Typography>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {job && <JobInfoCard job={job} />}
      {createFormComponent({
        config: formConfig,
        handleSubmit,
        loading: updateJobMutation.isPending,
        mode: 'edit',
      })}
    </div>
  );
};

interface JobInfoCardProps {
  job: ExtendedJobStatus;
}

const JobInfoCard = ({ job }: JobInfoCardProps) => {
  const stateVariant = JOB_STATE_VARIANTS[job.state] ?? 'secondary';
  const stateLabel = STATE_LABEL_MAP[job.state];

  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-col gap-2">
        <Typography variant="body" className="text-base font-semibold">
          Job #{job.id}
        </Typography>
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
          <span>{job.queue}</span>
          <span className="text-xs">•</span>
          <span className="capitalize">{stateLabel}</span>
          <Badge variant={stateVariant} className="capitalize">
            {stateLabel}
          </Badge>
        </div>
      </div>
    </Card>
  );
};
